import {AfterViewChecked, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {HelpDTO} from '../../../../other/models/HelpDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {KeycloakService} from 'keycloak-angular';
import {LookupService} from '../../../../common/services/lookup.service';
import {HelpService} from '../../../../other/services/help.service';
import {AddRefillDialogComponent} from '../add-refill-dialog/add-refill-dialog.component';
import {GrandfatherService} from '../../../services/grandfather.service';
import {GrandfatherDTO} from '../../../models/GrandfatherDTO';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {NationalDrugFormularyService} from '../../../services/national-drug-formulary.service';
import {NationalDrugFormularyDTO} from '../../../models/NationalDrugFormularyDTO';
import {Common, createFieldMap} from '../../../../common/util/Util';
import {SharedService} from '../../../../common/services/shared-service';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';

@Component({
  selector: 'app-add-grandfather-dialog',
  templateUrl: './add-grandfather-dialog.component.html',
  styleUrls: ['./add-grandfather-dialog.component.scss', '../../../../../../styles/global/_dialog-styles.scss']
})
export class AddGrandfatherDialogComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly lookupValue = LookupValue;

  nameFormControl: FormControl;
  policyFormControl: FormControl;
  insuredPersonFormControl: FormControl;
  formularyFormControl: FormControl;
  grandfatherFormGroup: FormGroup;

  helpInfo: HelpDTO[];
  policies: NameValueDTO[];
  insuredPersons: NameValueDTO[];
  editGrandfather = new GrandfatherDTO();
  isEdit = false;
  userFirstLastName = '';
  errorMessage = '';
  grandfatherApplications: string[] = ['Policy', 'Insured Person'];
  chosenGrandfatherApplication = 'Policy';
  formularyTextChanged = new Subject<string>();
  formularyNameSubscription: Subscription;
  selectedFormulary: NationalDrugFormularyDTO;
  filteredFormularies: NationalDrugFormularyDTO[];

  constructor(
    private changeDetect: ChangeDetectorRef,
    public dialogRef: MatDialogRef<AddRefillDialogComponent>,
    private formBuilder: FormBuilder,
    private grandfatherService: GrandfatherService,
    private keycloakService: KeycloakService,
    private lookupService: LookupService,
    private helpService: HelpService,
    private sharedService: SharedService,
    private nationalDrugFormularyService: NationalDrugFormularyService,
    @Inject(MAT_DIALOG_DATA) public id: number
  ) {
    this.nameFormControl = new FormControl('');
    this.policyFormControl = new FormControl('');
    this.insuredPersonFormControl = new FormControl('');
    this.formularyFormControl = new FormControl('');

    this.grandfatherFormGroup = this.formBuilder.group({
      name: this.nameFormControl,
      policy: this.policyFormControl,
      insuredPerson: this.insuredPersonFormControl,
      formulary: this.formularyFormControl
    });

    if (id != null) {
      this.isEdit = true;
      this.getGrandfather(id);
    }

    this.formularyNameSubscription = this.formularyTextChanged.pipe(
      debounceTime(this.debounceTime),
    ).subscribe(() => this.getFilteredListOfFormularies());
  }

  ngOnInit(): void {
    this.getListOfLookupValues(this.lookupValue.Policy);
    this.getListOfLookupValues(this.lookupValue.InsuredPerson);
    this.getHelpInfo('grandfather');
    this.getFilteredListOfFormularies(false);

    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });
  }

  ngOnDestroy(): void {
    this.formularyNameSubscription.unsubscribe();
  }

  ngAfterViewChecked(): void {
    this.changeDetect.detectChanges();
  }

  getGrandfather(id: number) {
    this.grandfatherService.getGrandfather(id).subscribe(
      response => {
        this.editGrandfather = response;
        this.nameFormControl.setValue(this.editGrandfather.name);
        this.policyFormControl.setValue(this.editGrandfather.policy != null ? this.editGrandfather.policy.name : '');
        this.insuredPersonFormControl.setValue(this.editGrandfather.insuredPerson != null ? this.editGrandfather.insuredPerson.name : '');
        this.formularyFormControl.setValue(this.editGrandfather.nationalDrugFormulary != null ? this.editGrandfather.nationalDrugFormulary.name : '');
        this.chosenGrandfatherApplication = this.editGrandfather.policy != null ? 'Policy' : 'Insured Person';
        this.disableGrandfatherFormField();
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.Policy) {
          this.policies = response;
        }
        else if (entity === this.lookupValue.InsuredPerson) {
          this.insuredPersons = response;
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName).subscribe(
      (response) => {
        this.helpInfo = response;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo != null) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo != null ? helpInfo.inputInfo : '';
    }
    return message;
  }

  getFilteredListOfFormularies(isFilter: boolean = true): void {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblNationalDrugFormulary;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 100;

    if (isFilter) {
      pagedRequestDTO.fieldMap = createFieldMap(
        'name',
        this.formularyFormControl.value
      );
    }

    this.nationalDrugFormularyService.getListOfNationalDrugFormularies(pagedRequestDTO).subscribe(
      (response) => {
        this.filteredFormularies = response.resultList;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  onSaveClick(): void {
    if (this.grandfatherFormGroup.valid) {
      const grandfatherDTO = new GrandfatherDTO();
      grandfatherDTO.name = this.nameFormControl.value;
      grandfatherDTO.policy = this.policies.find(
        (item) => item.name === this.policyFormControl.value
      );
      grandfatherDTO.insuredPerson = this.insuredPersons.find(
        (item) => item.name === this.insuredPersonFormControl.value
      );
      grandfatherDTO.nationalDrugFormulary = {value: this.selectedFormulary.nationalDrugFormularyId, name: this.selectedFormulary.name};

      if (this.isEdit) {
        grandfatherDTO.grandfatherId = this.editGrandfather.grandfatherId;
        grandfatherDTO.created = this.editGrandfather.created;
        grandfatherDTO.createdBy = this.editGrandfather.createdBy;
        grandfatherDTO.updated = new Date();
        grandfatherDTO.updatedBy = this.userFirstLastName;
      }
      else {
        grandfatherDTO.created = new Date();
        grandfatherDTO.createdBy = this.userFirstLastName;
      }

      this.grandfatherService.saveGrandfather(grandfatherDTO).subscribe(
        response => {
          this.sharedService.openSnackBar(`Grandfather ${response.name} saved successfully`, 'Dismiss');
          this.dialogRef.close();
        },
        errorResponse => {
          const error = errorResponse.error;
          this.errorMessage = error.businessMessage;
        }
      );
    }
    else {
      this.errorMessage = 'Please complete all required fields.';
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  disableGrandfatherFormField() {
    if (this.chosenGrandfatherApplication === 'Policy') {
      this.insuredPersonFormControl.setValue('');
      this.policyFormControl.setValue(
        this.editGrandfather.policy != null ? this.editGrandfather.policy.name : ''
      );

      this.insuredPersonFormControl.disable();
      this.policyFormControl.enable();
    }
    else if (this.chosenGrandfatherApplication === 'Insured Person') {
      this.insuredPersonFormControl.setValue(
        this.editGrandfather.insuredPerson != null ? this.editGrandfather.insuredPerson.name : ''
      );
      this.policyFormControl.setValue('');

      this.insuredPersonFormControl.enable();
      this.policyFormControl.disable();
    }
  }

  onInputChangedSearchFormularies($event) {
    this.formularyTextChanged.next($event.target.value);
  }

  blurFormulary() {
    if (
      this.filteredFormularies != null &&
      this.filteredFormularies.find(
        (item) => item.name === this.formularyFormControl.value
      ) == null
    ) {
      this.formularyFormControl.reset();
      this.selectedFormulary = null;
    }
  }

  formularySelected(name: string) {
    if (this.filteredFormularies != null) {
      this.selectedFormulary = this.filteredFormularies.find((item) => name === item.name);
    }
  }

  clearFormularyName() {
    this.formularyFormControl.reset();
    this.selectedFormulary = null;
    this.filteredFormularies = [];
  }

}
